import { Component, ComponentRef, NgZone, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { fadeInRightAnimation } from '../../../../@fury/animations/fade-in-right.animation';
import { fadeInUpAnimation } from '../../../../@fury/animations/fade-in-up.animation';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { ApiService } from '../../../services/base/api.service';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { Report } from '../../../interfaces/report/report';
import { LatLngLiteral, Layer, Marker, PopupEvent } from 'leaflet';
import { MarkerPopupComponent } from '../marker-popup/marker-popup.component';
import { LayoutService } from '../../../layout/layout.service';
import { ReportService } from '../../../services/reports/report.service';
import { LeafletMapService } from '../../../services/base/leaflet-map.service';
import { SidenavService } from '../../../layout/sidenav/sidenav.service';
import { FilterCategories } from '../../../interfaces/routering/filter-categories';

@Component({
  selector: 'fury-map-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss'],
  animations: [fadeInRightAnimation, fadeInUpAnimation],
})
export class IndexComponent implements OnInit, OnDestroy {

  private reports: Report[] = [];

  private formData: FormData = new FormData();

  private data$: Subject<null> = new Subject<null>();
  private destroy$: Subject<null> = new Subject<null>();

  constructor(private viewContainerRef: ViewContainerRef,
              private layoutService: LayoutService,
              private reportService: ReportService,
              private sidenavService: SidenavService,
              private zone: NgZone,
              public leafletMapService: LeafletMapService,
              public api: ApiService,
  ) {
  }

  ngOnInit(): void {
    this.layoutService.isLoading = false;

    this.api.generalHttpError.pipe(takeUntil(this.destroy$)).subscribe((result: boolean): void => {
      if (result) {
        this.layoutService.isLoading = false;
      }
    });

    this.sidenavService.selected$.subscribe((selected: FilterCategories[]): void => {
      this.formData = new FormData();
      if (selected) {
        selected.forEach((item: FilterCategories): void => {
          if (item.subIds.length) {
            item.subIds.forEach((d: number): void => {
              this.formData.append('subCategory[' + item.id + '][]', d + '');
            });
          }
        });

        this.getData();
      }
    });

    this.sidenavService.updateMap$.subscribe((coords: LatLngLiteral): void => {
      if (coords === null) {
        this.leafletMapService.setInitialView();
      } else {
        this.leafletMapService.setView(coords);
      }
    });

    this.data$.pipe(
      takeUntil(this.destroy$),
      switchMap(() => {
        this.layoutService.isLoading = true;

        return this.reportService.index(this.formData).pipe(
          takeUntil(this.destroy$),
          map((response: Report[]): void => {
            if (response) {
              response.forEach((report: Report): void => {
                if (typeof report.complaint !== 'undefined') {
                  if (typeof report.complaint.lat !== 'undefined' && typeof report.complaint.lng !== 'undefined') {
                    if (report.complaint.lat !== null && report.complaint.lng !== null) {
                      if (typeof this.reports.find((d: Report): boolean => d.id === report.id) === 'undefined') {
                        this.addMarker(report, [report.complaint.lat, report.complaint.lng], (report.head_category?.color ?? '#000000'));
                      }
                    }
                  }
                }
              });

              this.reports = response;
              this.clearMarkers();
            }

            this.leafletMapService.zoomToMarkers();

            this.layoutService.isLoading = false;
          }),
          catchError(() => {
            this.layoutService.isLoading = false;
            return EMPTY;
          })
        );
      }),
      catchError(() => {
        this.layoutService.isLoading = false;
        return EMPTY;
      })
    ).subscribe();
  }

  getData(): void {
    this.data$.next();
  }

  addMarker(data: Report, coords: [number, number], iconColor?: string): void {
    const marker: Marker = new Marker({lat: coords[0], lng: coords[1]}, {...this.leafletMapService.marker(iconColor), ...{id: data.id}});
    marker
      .bindPopup('', {
        autoPan: true,
        minWidth: 300,
        maxWidth: 500,
        keepInView: true,
      });

    let subscription: Subscription = null,
      factory: ComponentRef<MarkerPopupComponent> = null;

    marker.on('popupopen', (e: PopupEvent): void => {
      this.zone.run((): void => {
        factory = this.viewContainerRef.createComponent(MarkerPopupComponent);
        factory.instance.data = data;
        factory.instance.reports = this.reports;

        e.popup.setContent(factory.location.nativeElement);

        subscription = factory.instance.popupWidth$.subscribe((): void => {
          if (factory.instance.sameReports.length > 1) {
            e.popup.options.minWidth = 500;
          } else {
            e.popup.options.minWidth = 300;
          }
          e.popup.update();
        });
      });
    });

    marker.on('popupclose', (): void => {
      if (subscription !== null) {
        subscription.unsubscribe();
      }
      if (factory) {
        factory.destroy();
      }
    });

    marker
      .addTo(this.leafletMapService.markerLayer);
  }

  clearMarkers(): void {
    this.leafletMapService.markerLayer.getLayers().forEach((layer: Layer): void => {
      if (typeof this.reports.find((d: Report): boolean => d.id === (<any>layer.options).id) === 'undefined') {
        this.leafletMapService.markerLayer.removeLayer(layer);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.leafletMapService.clear();

    this.layoutService.isLoading = false;
  }
}
